import { graphql, useStaticQuery } from 'gatsby'
import PropTypes from 'prop-types'
import React from 'react'
import { removeDuplicates } from '../../../utils/siteHelper'
import { Grid, GridItem, GridItemDescription, GridItemTitle, PartnerGrid, PartnerGridImage, PartnerGridItem, PartnerGridLink, Wrapper } from './ProductGrid.style'

/**
  The new Product Grid lists product categories and partners with products in each category, emulating
  the UK website. We need to get the categories and partners from the products themselves as neither
  the partners or categories have a direct relationship with products in contentful (Products have both
  a partner and category in contentful as "brand" and "productCategoryGenre" respectively).
 */

const ProductGrid = ({ productGridType }) => {
  const allProducts = useStaticQuery(graphql`
    query {
      allContentfulProduct(
        sort: {
          fields: [title, transliteratedName]
          order: ASC
        }
      ) {
        edges {
          node {
            id
            title
            transliteratedName
            productType
            productCategoryGenre {
              ...GenreFragment
              ...ProductCategoryFragment
            }
            image {
              altText
              imageFile {
                file {
                  url
                }
              }
            }
            viewDetailsLink
            brand {
              brandName
              brandUrl
              slug
              id
              logo {
                fixed(width: 200) {
                  width
                  height
                  src
                  srcSet
                }
              }
            }
          }
        }
      }
    }
  `)

  // only get the products of the particular grid type
  const products = allProducts.allContentfulProduct.edges.filter(item => item.node.productType === productGridType)

  // get a list of all the categories from the products
  const categories = removeDuplicates(products.map((article) => {
    return {
      type: 'category',
      value: article.node.productCategoryGenre[0].title,
      fields: article.node.productCategoryGenre[0],
    }
  }), 'value')

  // sort order for the categories. This needs to be updated manually if any category name changes
  const categoriesOrder = ['ヘッドホン', 'ポータブルプレーヤー', 'DAC', 'ポータブルDAC', 'ネットワークストリーマー', 'スピーカー', 'リッパー', 'CDプレーヤー', 'Amplifier']

  const sortedCategories = categories.sort((a, b) => {
    return categoriesOrder.indexOf(a.value) - categoriesOrder.indexOf(b.value)
  })

  return (
    <Wrapper>
      <Grid>
        {sortedCategories.map((category) => {
          // get all partners with products in this category
          const allPartners = products
            .filter((product) => product.node.productCategoryGenre[0].title === category.value && product.node.brand)
            .map((product) => (product.node.brand))

          // remove duplicate partners and sort alphabetically
          const partners = removeDuplicates(allPartners, 'brandName').sort((a, b) => {
            const nameA = a.brandName.trim().toLowerCase()
            const nameB = b.brandName.trim().toLowerCase()
            if (nameA < nameB) return -1
            if (nameA > nameB) return 1
            return 0
          })

          return (
            <GridItem key={category.value}>
              {category.fields.image ? <img src={category.fields.image.fixed.src} alt={category.value} /> : null}
              <GridItemTitle>{category.value}</GridItemTitle>
              {category.fields.description && <GridItemDescription>{category.fields.description.internal.content}</GridItemDescription>}
              <PartnerGrid>
                {partners.map((partner) => (
                  <PartnerGridItem key={partner.id}>
                    <PartnerGridLink href={partner.slug ? `/partner/${partner.slug}` : null} title={partner.brandName}>
                      {partner.logo ? <PartnerGridImage src={partner.logo.fixed.src} alt={partner.brandName} /> : partner.brandName }
                    </PartnerGridLink>
                  </PartnerGridItem>
                ))}
              </PartnerGrid>
            </GridItem>
          )
        })}
      </Grid>
    </Wrapper>
  )
}

ProductGrid.propTypes = {
  productGridType: PropTypes.string.isRequired,
}

export const ProductGridFragment = graphql`
  fragment ProductGridFragment on ContentfulProductGrid {
    id
    productGridType
  }
`

export default ProductGrid
